// helpers
import { storeAnalyticsDataInLocal } from 'shared/helpers/storage/storeData';
import { AddToCartParams } from './types';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import { getHybridPDPNavigationForAddToCart } from 'shared/helpers/analytics/hybridPDPAttribution';
import { getDynamicCategoryLevels } from './components/ProductInfo/helpers';

// types
import { IMedia } from 'shared/store/details/types';
import { EVENT_NAMES, EVENT_TYPES } from 'shared/helpers/utils/impressions';

// constants
import isEmpty from 'lodash/isEmpty';
import { DEFAULT_CURRENCY_CODE } from 'shared/constants';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { PDP_EVENTS } from 'shared/helpers/analytics/constants/pdpEvents';
import { getMergedDataWithRetinaData } from 'shared/helpers/analytics/helpers';
import { getPincodeFromStorage } from 'shared/layouts/DetailsPage/components/ProductInfo/storage';
import { resolveVendorId } from 'shared/helpers/multiSeller';

const getTagString = (taglist: { [key: string]: string; }[] = []) => {
  if (taglist.length >= 0) return taglist.map((tag) => tag.title).join(',');

  return '';
};

const isVideoAvailableOnPDP = (medias: IMedia[] = []) => {
  let isVideoAvailable = false;

  medias.forEach((media: IMedia) => {
    if (media.mediaType === 'video') {
      isVideoAvailable = true;
    }
  });
  return isVideoAvailable;
};

const getAddToCartTrackingData = (
  productData: ProductDetail.ISizeOption | undefined,
  productType?: string,
  saleEnabled?: boolean,
  cartSpecialTag?: Record<string, any>,
  cartSale?: Record<string, any>,
  selectedVendor?: ProductDetail.IVendorDetails
) => {
  if (isEmpty(productData)) return {};

  const isSpecialTagsPresentInTopN = !!productData.specialTag;
  const specialTagName = isSpecialTagsPresentInTopN ? productData.specialTag.tag : '';
  let isPriceRevealProductAvailable = false;

  if (isEmpty(productData?.price_reveal_data) === false) {
    isPriceRevealProductAvailable = true;
  } else {
    isPriceRevealProductAvailable = false;
  }

  const defaultVendorId = resolveVendorId(
    productData?.view_more_section?.vendors,
    productData?.vendor_id,
    productData?.vendor_id
  );
  const defaultVendor = productData?.view_more_section?.vendors?.[defaultVendorId || ''];
  const defaultVendorEdd = productData?.edd_info?.skuDetails?.details?.[productData.sku || '']?.[
    defaultVendorId || ''
  ];

  return {
    productID: productData?.parentId || productData?.id,
    productName: productData?.subTitle,
    productBrandName: productData?.title || productData?.brandName[0],
    currency: DEFAULT_CURRENCY_CODE,
    MRP: selectedVendor?.price || productData?.price || productData?.mrp,
    discountedPrice: selectedVendor?.discountedPrice || productData?.discountedPrice || productData?.finalPrice,
    discountPercent: selectedVendor?.discount ?? productData?.discount,
    buy_box_edd_o2d: defaultVendorEdd?.o2dTat,
    buy_box_edd_o2s: defaultVendorEdd?.o2sTat,
    buy_box_edd_timestamp: defaultVendorEdd?.estimatedDeliveryTime,
    buy_box_discounted_percent: defaultVendor?.discount ?? productData?.discount,
    buy_box_discounted_price: defaultVendor?.discountedPrice ?? productData?.discountedPrice,
    buy_box_mrp_price: defaultVendor?.price ?? productData?.price,
    productTags: getTagString(productData?.tag_list),
    isVideoAvailable: isVideoAvailableOnPDP(productData?.productMedia),
    disclaimerTextShown: productData?.disclaimer || false,
    // should we show this on wishlist as we would not see this
    ...getDynamicCategoryLevels(productData?.primary_categories_json_all),
    variantType: productData?.sizeName ? productData?.sizeName : undefined,
    variantProductID: productData?.sizeName ? productData?.id : undefined,
    productColorName: productData?.color?.name,
    ...(productType ? { productType } : {}),
    ...(specialTagName ? { specialTagName } : ''),
    ...(isSpecialTagsPresentInTopN ? { isSpecialTagsPresentInTopN } : false),
    isPriceRevealProductAvailable,
    isSalePriceLive: saleEnabled || false,
    specialTag: (productData?.specialTag != null || cartSpecialTag != null) ? (productData?.specialTag?.type || cartSpecialTag?.type) : "",
    specialTagEnabled: productData?.specialTag != null || cartSpecialTag != null,
    specialTagType: (productData?.specialTag != null || cartSpecialTag != null) ? (productData?.is_sale_participating ? 'MRP/DP' : cartSale ? 'CART Rule' : "") : "",
    priceParticipatingSkuType: (productData?.is_sale_participating || cartSale) ? (productData?.is_sale_participating ? 'MRP/DP' : cartSale ? 'CART Rule' : "") : "",
    isPriceParticipatingSku: (productData?.is_sale_participating || cartSale) ? true : false,
    ...(productData?.catalog_tag ? { catalog_tags: productData.catalog_tag } : {}),
    ...(productData?.store ? { catalog_tag_filter: productData.store } : {}),
  };
};

export const onAddToCartClick = ({
  addToCartLocation = NFA.pageProperties.pagetype,
  productData,
  productType,
  saleEnabled,
  cartSpecialTag,
  cartSale,
  returnNudgeApplicable,
  returnNudgeVisible,
  selectedVendorCode,
  selectedVendorType,
  availableVendorCode,
  availableVendorType,
  isBuyBoxWinner,
  selectedVendorId: selectedVendorIdProp,
  analyticsContext,
}: AddToCartParams) => {
  // Check for 'source' URL parameter
  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const source = urlParams.get('source');
  const availableVendors = Object.values(productData?.view_more_section?.vendors || {});
  const selectedVendorId = resolveVendorId(
    productData?.view_more_section?.vendors,
    selectedVendorIdProp || productData?.vendor_id,
    productData?.vendor_id
  );
  const selectedVendor = productData?.view_more_section?.vendors?.[selectedVendorId || ''];
  const selectedVendorEdd = productData?.edd_info?.skuDetails?.details?.[productData?.sku || '']?.[selectedVendorId || ''];
  const selectedVendorPrice = selectedVendor?.price ?? productData?.price;
  const selectedVendorDiscountedPrice = selectedVendor?.discountedPrice ?? productData?.discountedPrice;

  const retinaData = {
    product_brand: productData?.title,
    product_dp: selectedVendorDiscountedPrice,
    product_id: productData?.id,
    product_mrp: selectedVendorPrice,
    product_price: selectedVendorDiscountedPrice,
    product_tags: productData?.tag_list
      ? productData?.tag_list.map((item) => item.title)
      : undefined,
    product_name: productData?.subTitle,
    product_currency: DEFAULT_CURRENCY_CODE,
    product_sku: productData?.sku,
    pincode: typeof window !== 'undefined' ? getPincodeFromStorage() : '',
    vendor_code: selectedVendorId,
    vendor_type: selectedVendor?.vendor_type ?? productData?.vendor_type,
    event_name: EVENT_NAMES.ADD_TO_CART,
    event_type: EVENT_TYPES.CLICK,
  };

  const data = {
    ...getAddToCartTrackingData(productData, productType, saleEnabled, cartSpecialTag, cartSale, selectedVendor),
    source: source || 'pdp',
    add_to_cart_location: addToCartLocation,
    returns_nudge_applicable: returnNudgeApplicable || false,
    returns_nudge_visible: returnNudgeVisible || false,
    selected_vendor_code:
      selectedVendorCode ?? selectedVendorId,
    selected_vendor_type:
      selectedVendorType ?? selectedVendor?.vendor_type ?? productData?.vendor_type,
    available_vendor_code:
      availableVendorCode ?? Object.keys(productData?.view_more_section?.vendors || {}),
    available_vendor_type:
      availableVendorType ?? availableVendors.map((vendor) => vendor.vendor_type),
    is_buy_box_winner: isBuyBoxWinner ?? selectedVendorId === productData?.vendor_id,
    available_vendor_count: availableVendors.length,
    edd_delivery_message: selectedVendorEdd?.message ?? selectedVendor?.edd_message,
    edd_delivery_type: selectedVendorEdd?.deliveryType,
    edd_o2d: selectedVendorEdd?.o2dTat,
    edd_o2s: selectedVendorEdd?.o2sTat,
    edd_timestamp: selectedVendorEdd?.estimatedDeliveryTime,
    ...getHybridPDPNavigationForAddToCart(String(productData?.parentId || productData?.id || '')),
    ...analyticsContext,
    ...(analyticsContext && productData?.sizeName ? {
      variantProductID: productData.id,
      variantType: productData.sizeName,
      MRP: productData.price ?? productData.mrp,
      discountedPrice: productData.discountedPrice ?? productData.finalPrice,
      discountPercent: productData.discount,
    } : {}),
  };

  NFA.track({
    event: PDP_EVENTS.ADD_TO_CART,
    type: TRACK_TYPE.EVENT,
    data: getMergedDataWithRetinaData(retinaData, data),
  });
};

export const sizeSelectorLoaded = (
  catalogTags?: string[], catalogTagFilter?: string, analyticsContext?: Record<string, unknown>
) => {
  NFA.track({
    event: PDP_EVENTS.SIZE_SELECTOR_LOADED,
    type: TRACK_TYPE.EVENT,
    ...(catalogTags || catalogTagFilter || analyticsContext
      ? {
        data: {
          ...analyticsContext,
          ...(catalogTags ? { catalog_tags: catalogTags } : {}),
          ...(catalogTagFilter ? { catalog_tag_filter: catalogTagFilter } : {}),
        },
      }
      : {}),
  });
};

export const trackPDPColorVariantClicked = (colorVariantClicked: string) => {
  storeAnalyticsDataInLocal({ PDPColorVariantClicked: colorVariantClicked });
  NFA.track({
    event: PDP_EVENTS.PDP_COLOR_SELECTED,
    type: TRACK_TYPE.EVENT,
    data: {
      colorVariantClicked,
    },
  });
};

export const trackBrandNameClicked = (location: string) => {
  NFA.track({
    event: PDP_EVENTS.BRAND_NAME_CLICKED,
    type: TRACK_TYPE.EVENT,
    data: {
      brandNameLocation: location,
    },
  });
};

export const trackCollapsibleSectionClicked = (sectionName: string) => {
  NFA.track({
    event: PDP_EVENTS.INFO_SECTION_CLICKED,
    type: TRACK_TYPE.EVENT,
    data: {
      expandedProductInfoSectionName: sectionName,
    },
  });
};

export const trackBestPriceShown = (isBestPriceShown: boolean, isBestPriceEligible: boolean) => {
  NFA.updateDataLayer({
    isBestPriceEligible: isBestPriceEligible,
    isBestPriceShown: isBestPriceShown,
  });
};
